// Двете измерения, по които се чете одитът: МОДУЛ (къде) и ВИД ВЪЗДЕЙСТВИЕ
// (колко тежи). Стойностите ги смята БЕКЕНДЪТ и пътуват в реда — тук стои
// само превръщането им до етикет и тон, плюс подредбата им в UI-я.
//
// Затова този файл няма нито един път: сменя ли се таксономията, сменя се
// `app/features/audit_logs/classify.py`, не тук.

import type { CellTone } from "../../../../components/dataTable";
import type { AuditLogImpact, AuditLogModule } from "../../../../api/auditLogs";

/** Подредбата в чиповете и в разбивката — от най-често гледаното надолу. */
export const AUDIT_MODULES: readonly AuditLogModule[] = [
  "ordering",
  "cash",
  "inventory",
  "invoicing",
  "reports",
  "configuration",
  "access",
  "system",
] as const;

export const AUDIT_IMPACTS: readonly AuditLogImpact[] = [
  "money",
  "settings",
  "data",
  "read",
] as const;

export const moduleLabelKey = (module: AuditLogModule) =>
  `auditLog.module.${module}`;

export const impactLabelKey = (impact: AuditLogImpact) =>
  `auditLog.impact.${impact}`;

/**
 * Тонът на чипа „Вид". Само парите и настройките получават цвят — те са
 * въпросите, заради които се отваря този екран. Останалите два вида биха
 * оцветили всеки ред и биха изгубили точно тези, които трябва да изпъкнат.
 */
export const IMPACT_TONE: Record<AuditLogImpact, CellTone | null> = {
  money: "brand",
  settings: "info",
  data: null,
  read: null,
};

export const METHOD_TONE: Record<string, CellTone> = {
  GET: "neutral",
  POST: "ok",
  PUT: "info",
  PATCH: "warn",
  DELETE: "danger",
};

/** Мутациите — стойността на филтъра, когато превключвателят „четения" е долу. */
export const WRITE_METHODS = ["POST", "PUT", "PATCH", "DELETE"] as const;

/** Над този праг заявката се чете като бавна и се оцветява. */
export const SLOW_MS = 1000;

/**
 * Изходът на действието. HTTP 2xx с непечатан бон Е провал — за оператора
 * няма разлика между „сървърът отказа" и „бонът не излезе".
 */
export type AuditOutcome = "ok" | "failed" | "rejected" | "error";

export const outcomeOf = (entry: {
  status_code: number;
  fiscal_failed: boolean;
}): AuditOutcome => {
  if (entry.status_code >= 500) return "error";
  if (entry.status_code >= 400) return "rejected";
  if (entry.fiscal_failed) return "failed";
  return "ok";
};

export const OUTCOME_TONE: Record<AuditOutcome, CellTone> = {
  ok: "ok",
  failed: "danger",
  rejected: "warn",
  error: "danger",
};

export const outcomeLabelKey = (outcome: AuditOutcome) =>
  `auditLog.outcome.${outcome}`;

/**
 * Ид-то на записа, върху който е действано, извлечено от пътя
 * (`/inventory/deliveries/42` → `42`). За модулите извън „Маркиране" това е
 * единственият идентификатор в реда — `bill_id` се пълни само за `/bills/*`.
 */
export const entityIdOf = (path: string): string | null => {
  const segments = path.split("?")[0].split("/").filter(Boolean);
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    if (/^\d+$/.test(segments[index])) return segments[index];
  }
  return null;
};
